import { RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { round } from 'lodash';

type UseSliderProps = {
  maxValue: number;
  startingValue: number | undefined;
  ref: RefObject<HTMLDivElement>;
};

const markerWidth = 32;

export const useSlider = ({ maxValue, startingValue, ref }: UseSliderProps) => {
  const width = ref.current?.parentElement?.offsetWidth || 0;
  const prevWidthRef = useRef<number>(width);
  const [percentageValue, setPercentageValue] = useState<number>(0);

  const markerPercent = useMemo(() => {
    if (width === 0) return 60; // mobile view should be 70
    return ((width - markerWidth * 3) / width) * 100 - 3;
  }, [width]);

  const maxMarkerPercent = useMemo(() => {
    if (width === 0) return 80;
    return ((width - markerWidth * 2) / width) * 100 - 2;
  }, [width]);

  const plusOneMarkerPercent = useMemo(() => {
    if (width === 0) return 90;
    return ((width - markerWidth) / width) * 100 - 1;
  }, [width]);

  const baseStepValue = useMemo(() => {
    const stepValue = maxValue > 1 ? markerPercent / (maxValue - 1) : maxMarkerPercent;

    // Round the step value to 2 decimal places
    const roundedStepValue = parseFloat(stepValue.toFixed(2));

    // Ensure we never return 0. If roundedStepValue is 0, return a small value to avoid division by zero.
    return roundedStepValue || 0.01;
  }, [markerPercent, maxMarkerPercent, maxValue]);

  const computeHandleLabel = useCallback(
    (value: number) => {
      if (Math.round(value) === 100) {
        return '+2';
      } else if (Math.round(value) === Math.round(plusOneMarkerPercent)) {
        return '+1';
      } else if (Math.round(value) === Math.round(maxMarkerPercent)) {
        return `${maxValue}`;
      } else {
        return `${round(value / baseStepValue)}`;
      }
    },
    [baseStepValue, maxMarkerPercent, maxValue, plusOneMarkerPercent]
  );

  const setHandleLabel = useCallback(
    (value: number) => {
      if (ref.current) {
        const el = ref.current.getElementsByClassName('rc-slider-handle')[0];
        if (el) {
          el.textContent = computeHandleLabel(value);
        }
      }
    },
    [computeHandleLabel, ref]
  );

  const onChangeHandler = useCallback(
    (value: number, onChangeCallback: (sliderValue: number) => void) => {
      setPercentageValue(value);
      setHandleLabel(value);
      let sliderValue: number;
      switch (value) {
        case 100:
          sliderValue = maxValue + 2;
          break;
        case plusOneMarkerPercent:
          sliderValue = maxValue + 1;
          break;
        case maxMarkerPercent:
          sliderValue = maxValue;
          break;
        case 70:
          sliderValue = maxValue;
          break;
        default:
          sliderValue = round(value / baseStepValue);
      }

      onChangeCallback(sliderValue);
    },
    [baseStepValue, maxMarkerPercent, maxValue, plusOneMarkerPercent, setHandleLabel, setPercentageValue]
  );

  const startValue = useCallback(() => {
    let valueToSet: number;

    if (startingValue !== undefined) {
      if (startingValue === maxValue + 2) {
        valueToSet = 100;
      } else if (startingValue === maxValue + 1) {
        valueToSet = plusOneMarkerPercent;
      } else if (startingValue === maxValue) {
        valueToSet = maxMarkerPercent;
      } else {
        valueToSet = startingValue * baseStepValue;
      }
    } else {
      valueToSet = maxValue <= 6 ? maxMarkerPercent : 6 * baseStepValue;
    }

    setPercentageValue(valueToSet);
    setHandleLabel(valueToSet);
  }, [maxValue, startingValue, setHandleLabel, maxMarkerPercent, baseStepValue, plusOneMarkerPercent]);

  // We only update when a confirmed change in width happens
  // because we need the markers and width to be calculated properly
  useLayoutEffect(() => {
    if (width !== prevWidthRef.current) {
      startValue();
      prevWidthRef.current = width;
    }
  }, [startValue, width]);

  // Always run starValue on mount
  // useDidMount(() => {
  //   startValue();
  // });

  useEffect(() => {
    startValue();
  }, [startValue]);

  return {
    maxValue,
    percentageValue,
    maxMarkerPercent,
    plusOneMarkerPercent,
    baseStepValue,
    setHandleLabel,
    onChangeHandler
  };
};
